/**
 * One row of the `deriveLocked` truth table (TR-7).
 *
 * Exported so the test can iterate the rows AND assert their count. A case list
 * the test merely reads would let a dropped row become a silently untested
 * branch; asserting `length` is what makes "all four rows" a mechanism rather
 * than a claim.
 */
export interface DeriveLockedCase {
    readonly lockedBy: string | null;
    readonly currentUserUid: string | null;
    readonly expected: boolean;
    readonly meaning: string;
}

/**
 * The complete truth table. Both uid columns are TRANSPORT-form values (the
 * 22-character ShortId) — `lockedBy` arrives that way from the Linkview
 * serializer and `currentUserUid` that way from the auth responses, so the
 * comparison is a plain `===` between two strings of the same form.
 */
export const DERIVE_LOCKED_CASES: readonly DeriveLockedCase[] = [
    {
        lockedBy: null,
        currentUserUid: 'AZq4Jw8kR0KJmXtP2vNbHQ',
        expected: true,
        meaning: 'nobody holds it -> view mode, click "Bearbeiten" to acquire',
    },
    {
        lockedBy: 'AZq4Jw8kR0KJmXtP2vNbHQ',
        currentUserUid: 'AZq4Jw8kR0KJmXtP2vNbHQ',
        expected: false,
        meaning: 'I hold it -> edit mode',
    },
    {
        lockedBy: 'BXt7Lm2pQ9CDnRvW4yZaKG',
        currentUserUid: 'AZq4Jw8kR0KJmXtP2vNbHQ',
        expected: true,
        meaning: 'somebody else holds it -> view mode',
    },
    {
        lockedBy: 'BXt7Lm2pQ9CDnRvW4yZaKG',
        currentUserUid: null,
        expected: true,
        meaning: 'own uid unknown -> fail closed',
    },
];

/**
 * Whether the board must render in VIEW mode for the asking user.
 *
 * MIND THE POLARITY: `true` means view mode (not draggable), `false` means edit
 * mode. It is the inverse of the everyday reading of the word "locked", and it
 * is the pre-existing convention this feature had to fit rather than change.
 *
 * The one thing this function exists to prevent: `locked` is NOT
 * `lockedAt !== null`. That naive form asks "is anybody editing?" when the
 * question is "is anybody OTHER THAN ME editing?", and it inverts the answer for
 * everyone — the moment one person acquires the lock, the grid becomes draggable
 * for every OTHER user and static for the holder, which is precisely backwards.
 * Hence the comparison against the asking user's own uid, and hence the uid has
 * to be plumbed to the client at all.
 *
 * IT FAILS CLOSED. When `currentUserUid` is null the client cannot answer "do I
 * hold this?", so it answers "no" and shows view mode. That converts a silent
 * failure — the uid never reaching the client, which no type-check gate would
 * catch, since `vite build` is esbuild and transpile-only — into an immediate
 * and visible one: nothing is editable, rather than everything being editable by
 * everybody.
 *
 * @param lockedBy       the holder's uid, transport form, or null when free
 * @param currentUserUid the asking user's own uid, transport form, or null when unknown
 */
export function deriveLocked(lockedBy: string | null, currentUserUid: string | null): boolean {
    if (currentUserUid === null) {
        return true;
    }

    if (lockedBy === null) {
        return true;
    }

    return lockedBy !== currentUserUid;
}
